import { useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import * as Cesium from 'cesium';
import MapPortalService from '../services/MapPortal/mapportal.service';
import { useMutation } from '@tanstack/react-query';
import { useLayerProperitesContext } from '../context/LayerPropertiesContext';
import { ApiRoutes } from '../routes/routeConstants/apiRoutes';
import { removeExistingGlowEntities, renderGlowOutline, renderGreyscaleLine, renderGreyscalePolygon } from '../shared/utils/effectsHelpers';
import { buildHighlightSld } from '../shared/utils/toolHighlightSld';

export interface UseCesiumEffectsArgs {
  isViewerReady: boolean;
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  searchableFeatures: any;
}

export const useCesiumEffects = ({ isViewerReady, viewerRef, searchableFeatures }: UseCesiumEffectsArgs) => {
  const highlightRef = useRef<Cesium.Entity[]>([]);

  const { fetchFeaturesFromLayer } = MapPortalService();
  const { mutateAsync: fetchFeaturesFromLayerAsync } = useMutation(fetchFeaturesFromLayer());

  const fetchLayerFeatures = useCallback(async (layerName: string) => fetchFeaturesFromLayerAsync({ layerName }), [fetchFeaturesFromLayerAsync]);

  const {
    symbologyUniqueKey,
    symbologyUniqueValue,
    uniqueFill,
    uniqueFillOpacity,
    uniqueOutline,
    uniqueOutlineType,
    uniqueOutlineOpacity,
    uniqueOutlineWeight,
    glow,
    glowColor,
    glowStrength,
    glowRadius,
    greyScale,
    uniqueLayerName,
    layerType,
    selectedFeatureIds,
    selectionCql,
    highlightColor,
  } = useLayerProperitesContext();

  const selectionHighlightsRef = useRef<Cesium.Entity[]>([]);
  // P4 — the server-rendered WMS highlight overlay (replaces per-feature Cesium
  // entities so a 50k selection costs O(1) on the client).
  const highlightImageryRef = useRef<Cesium.ImageryLayer | null>(null);

  const highlightUniqueFeature = useCallback(
    (matchingFeatures: any[]) => {
      const viewer = viewerRef.current;
      if (!viewer || !matchingFeatures?.length) return;

      viewer.entities.removeAll();
      matchingFeatures.forEach((feature: any) => {
        let coordinates = feature.geometry.coordinates;
        if (feature.geometry.type === 'MultiPolygon') {
          coordinates = coordinates.flat(1);
          const positions = coordinates[0].map((coord: number[]) => Cesium.Cartesian3.fromDegrees(coord[0], coord[1]));
          const fillColor = Cesium.Color.fromCssColorString(uniqueFill).withAlpha(uniqueFillOpacity / 100);
          viewer.entities.add({
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy(positions),
              material: fillColor,
              outline: false,
            },
          });
          if (uniqueOutlineType !== 'none') {
            const outlineColor = Cesium.Color.fromCssColorString(uniqueOutline).withAlpha(uniqueOutlineOpacity / 100);
            let material: any;
            if (uniqueOutlineType === 'solid') {
              material = outlineColor;
            } else if (uniqueOutlineType === 'dashed' || uniqueOutlineType === 'dotted') {
              material = new Cesium.PolylineDashMaterialProperty({
                color: outlineColor,
                dashLength: uniqueOutlineType === 'dashed' ? 16 : 4,
                gapColor: Cesium.Color.TRANSPARENT,
              });
            }
            const polylinePositions = [...positions, positions[0]];
            const weight = Math.max(0, Number(uniqueOutlineWeight) || 1);

            viewer.entities.add({
              polyline: {
                positions: polylinePositions,
                width: weight / 2,
                material: material,
                clampToGround: true,
              },
            });
          }
          const boundingRectangle = Cesium.Rectangle.fromCartesianArray(positions);
          viewer.camera.flyTo({ destination: boundingRectangle, duration: 2 });
        }
      });
    },
    [viewerRef, uniqueFill, uniqueFillOpacity, uniqueOutline, uniqueOutlineOpacity, uniqueOutlineType, uniqueOutlineWeight],
  );

  const highlightFeature = useCallback(
    (feature: any) => {
      if (!viewerRef.current) return;
      const viewer = viewerRef.current;

      if (highlightRef.current.length > 0) {
        highlightRef.current.forEach((entity) => {
          viewer.entities.remove(entity);
        });
        highlightRef.current = [];
      }

      const geom = feature?.data?.geometry || feature?.data?.geom;
      if (!geom) return;

      const newEntities: Cesium.Entity[] = [];

      if (geom.type === 'Point') {
        const [lon, lat] = geom.coordinates;
        const entity = viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          point: {
            pixelSize: 20,
            color: Cesium.Color.ORANGE.withAlpha(0.9),
            outlineColor: Cesium.Color.ORANGE,
            outlineWidth: 3,
          },
        });
        newEntities.push(entity);
      } else if (geom.type === 'MultiPoint') {
        const positions = geom.coordinates.map(([lon, lat]: number[]) => Cesium.Cartesian3.fromDegrees(lon, lat));
        positions.forEach((pos: any) => {
          const entity = viewer.entities.add({
            position: pos,
            point: {
              pixelSize: 20,
              color: Cesium.Color.ORANGE.withAlpha(0.9),
              outlineColor: Cesium.Color.ORANGE,
              outlineWidth: 3,
            },
          });
          newEntities.push(entity);
        });
      } else if (geom.type === 'LineString') {
        const positions = geom.coordinates.map(([lon, lat]: number[]) => Cesium.Cartesian3.fromDegrees(lon, lat));
        const entity = viewer.entities.add({
          polyline: { positions, width: 6, material: Cesium.Color.ORANGE },
        });
        newEntities.push(entity);
      } else if (geom.type === 'MultiLineString') {
        geom.coordinates.forEach((line: any) => {
          const positions = line.map(([lon, lat]: number[]) => Cesium.Cartesian3.fromDegrees(lon, lat));
          const entity = viewer.entities.add({
            polyline: { positions, width: 6, material: Cesium.Color.ORANGE },
          });
          newEntities.push(entity);
        });
      } else if (geom.type === 'Polygon') {
        const positions = geom.coordinates[0].map(([lon, lat]: number[]) => Cesium.Cartesian3.fromDegrees(lon, lat));
        const entity = viewer.entities.add({
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(positions),
            material: Cesium.Color.ORANGE.withAlpha(0.4),
            outline: true,
            outlineColor: Cesium.Color.ORANGE,
          },
        });
        newEntities.push(entity);
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((polygon: any) => {
          const positions = polygon[0].map(([lon, lat]: number[]) => Cesium.Cartesian3.fromDegrees(lon, lat));
          const entity = viewer.entities.add({
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy(positions),
              material: Cesium.Color.ORANGE.withAlpha(0.4),
              outline: true,
              outlineColor: Cesium.Color.ORANGE,
            },
          });
          newEntities.push(entity);
        });
      }

      highlightRef.current = newEntities;
    },
    [viewerRef],
  );

  // Remove the click-popup highlight (called when the popup closes).
  const clearHighlight = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;
    highlightRef.current.forEach((entity) => {
      try {
        viewer.entities.remove(entity);
      } catch {
        /* viewer already torn down */
      }
    });
    highlightRef.current = [];
  }, [viewerRef]);

  // 3. Unique Feature
  useEffect(() => {
    if (!viewerRef.current || !searchableFeatures?.length || !symbologyUniqueKey || !symbologyUniqueValue || !uniqueFill) return;
    const matchingFeatures = searchableFeatures.filter((feature: any) => feature.properties?.[symbologyUniqueKey] === symbologyUniqueValue);
    if (matchingFeatures.length > 0) highlightUniqueFeature(matchingFeatures);
  }, [
    symbologyUniqueKey,
    symbologyUniqueValue,
    uniqueFill,
    uniqueFillOpacity,
    uniqueOutline,
    uniqueOutlineType,
    uniqueOutlineOpacity,
    uniqueOutlineWeight,
    isViewerReady,
  ]);

  // 4. Glow/Greyscale
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !uniqueLayerName) return;
    let isMounted = true;

    const applyGlowAndGreyscale = async () => {
      try {
        removeExistingGlowEntities(viewer, uniqueLayerName);
        if (!glow && !greyScale) return;
        const featuresRes = await fetchLayerFeatures(uniqueLayerName);
        if (!isMounted) return;
        const layerFeatures =
          featuresRes?.features?.map((f: any) => ({
            ...f,
            layerName: uniqueLayerName,
          })) || [];
        if (!layerFeatures.length) return;
        const glowPower = Math.min(Math.max((Number(glowStrength) || 50) / 100, 0.1), 1.0);
        const widthPx = Math.max(1, Number(glowRadius) || 1);
        layerFeatures.forEach((feature: any, idx: number) => {
          if (!isMounted) return;
          if (greyScale) {
            const gray = Cesium.Color.GRAY.withAlpha(1.0);
            if (feature.geometry.type.includes('Line')) renderGreyscaleLine(viewer, feature, gray, uniqueLayerName, idx);
            else renderGreyscalePolygon(viewer, feature, gray, uniqueLayerName, idx);
          }
          if (glow) renderGlowOutline(viewer, feature, glowColor, glowPower, widthPx, uniqueLayerName, idx);
        });
      } catch (err) {
        console.error('applyGlowAndGreyscale error:', err);
      }
    };

    applyGlowAndGreyscale();

    return () => {
      isMounted = false;
      try {
        if (viewerRef.current) removeExistingGlowEntities(viewerRef.current, uniqueLayerName);
      } catch (e) {}
    };
  }, [glow, glowColor, glowStrength, glowRadius, greyScale, uniqueLayerName, isViewerReady]);

  // P4 — selection highlight rendered SERVER-SIDE as a WMS overlay with a
  // CQL_FILTER, instead of building one Cesium entity per selected feature.
  // Cost on the client is O(1) regardless of how many features are selected, so
  // "select all 50k matching" highlights instantly with no geometry download.
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current) return;
    const viewer = viewerRef.current;

    // Tear down any previous highlight overlay (and legacy entities).
    if (highlightImageryRef.current) {
      viewer.scene.imageryLayers.remove(highlightImageryRef.current, true);
      highlightImageryRef.current = null;
    }
    selectionHighlightsRef.current.forEach((e) => viewer.entities.remove(e));
    selectionHighlightsRef.current = [];

    // Resolve the layer + CQL: predicate ("select all matching") wins; otherwise
    // an explicit gid set from the selected feature ids ("<layer>.<gid>").
    let cql: string | null = null;
    let layer = uniqueLayerName;
    if (selectionCql) {
      cql = selectionCql;
    } else if (selectedFeatureIds && selectedFeatureIds.length > 0) {
      const gids = selectedFeatureIds.map((id) => String(id).split('.').pop()).filter((g) => g && /^\d+$/.test(g));
      if (gids.length) {
        cql = `gid IN (${gids.join(',')})`;
        const prefix = String(selectedFeatureIds[0]).split('.').slice(0, -1).join('.');
        if (prefix) layer = prefix;
      }
    }
    if (!cql || !layer) return;

    // A gid-derived layer may differ from the one loaded in context; its geometry
    // would then be wrong, so let the filtered rules resolve it instead.
    const sldBody = buildHighlightSld(layer, layer === uniqueLayerName ? layerType : undefined, highlightColor);

    const WMS_PATH = (import.meta as any).env?.VITE_APP_GEOSERVER_WMS_PATH || '/geoserver/mapaki/wms';
    const provider = new Cesium.WebMapServiceImageryProvider({
      url: `${ApiRoutes.GEOSERVER_BASE_URL}${WMS_PATH}`,
      layers: `mapaki:${layer}`,
      parameters: {
        service: 'WMS',
        version: '1.1.1',
        request: 'GetMap',
        transparent: true,
        format: 'image/png',
        tiled: true,
        cql_filter: cql,
        sld_body: sldBody,
      },
    });
    const imageryLayer = new Cesium.ImageryLayer(provider);
    viewer.scene.imageryLayers.add(imageryLayer); // on top of base + data layers
    highlightImageryRef.current = imageryLayer;

    return () => {
      if (highlightImageryRef.current && viewerRef.current) {
        viewerRef.current.scene.imageryLayers.remove(highlightImageryRef.current, true);
        highlightImageryRef.current = null;
      }
    };
  }, [selectedFeatureIds, selectionCql, uniqueLayerName, layerType, highlightColor, isViewerReady]);

  return { highlightFeature, clearHighlight };
};
